import { axios } from './axiosClient';
import { validate } from '@/utils/errors/helpers';
import { getDynamicRangesWithConditionsSchema } from '@/types/schemas/materials/getDynamicRangesWithConditions';
import type { RangeItem, Range, RangeCustomerTradersite, DynamicRange } from '@/types/Ranges';
import { getRangesSchema, range } from '@/types/schemas/materials/getRanges';
import { RecordMode, type TableFilterData } from '@/types/Base';
import { getRangeItemsSchema } from '@/types/schemas/materials/getRangeItemsSchema';
import { getRangeCustomerTradersitesSchema } from '@/types/schemas/materials/getRangeCustomerTradersites';
import { v4 as uuidv4 } from 'uuid';
export async function getRanges(): Promise<Range[]> {
  const response = await axios.get('v2/materials/ranges');
  return validate(getRangesSchema, response.data.data);
}

export async function removeRangesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/materials/ranges/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function getRangeById(id: string): Promise<Range | null> {
  const response = await axios.get(`/v2/materials/ranges/${id}`, {
    validateStatus: (status: number) => status === 404 || status == 200,
  });
  if (response.status === 404) return null;
  return validate(range, response.data);
}

export async function getRangeItemsWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  rangeId = null,
}: TableFilterData & { rangeId?: string | null }): Promise<{
  data: RangeItem[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  let url = `/v2/materials/ranges/items/search?take=${itemsPerPage}&skip=${skip}`;
  if (rangeId != null) url += `&rangeId=${rangeId}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    rangeId,
  });

  const valid = validate(getRangeItemsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function addEditRange(range: Range) {
  const response: any = await axios.post('/v2/materials/ranges', range);
  if (response.status === 201) return true;
  return false;
}

export async function addEditRangeItems(rangeItems: RangeItem[]) {
  const response: any = await axios.post('/v2/materials/ranges/items', rangeItems);
  if (response.status === 201) return true;
  return false;
}

export async function getDynamicRangesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query,
  sortColumn,
  sortAscending,
  groupItemId,
  traderId,
  pricingPolicyId,
  withoutPricingPolicy,
}: TableFilterData & {
  groupItemId?: string;
  traderId?: string;
  pricingPolicyId?: string;
  withoutPricingPolicy?: boolean;
}): Promise<{
  data: DynamicRange[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;
  const response = await axios.post(
    `/v2/materials/dynamic-ranges/search?take=${itemsPerPage}&skip=${skip}`,
    {
      searchText: query,
      sortColumn,
      sortAscending,
      groupItemId,
      traderId,
      pricingPolicyId,
      withoutPricingPolicy,
    }
  );
  const valid = validate(getDynamicRangesWithConditionsSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function editDynamicRange(dynamicRange: DynamicRange) {
  const response = await axios.post(`/v2/materials/dynamic-ranges`, dynamicRange);
  if (response.status === 201) return true;
  return false;
}

export async function removeDynamicRangesByIds(ids: string[]): Promise<string | null> {
  const response = await axios.post('/v2/materials/dynamic-ranges/delete-by-ids', ids, {
    validateStatus: (status: number) => status === 400 || status == 204,
  });
  if (response.status === 400)
    return response.data?.error?.code || response.data?.title || 'Unknown error occurred';
  else return null;
}

export async function addEditRangeCustomerTradersites(items: RangeCustomerTradersite[]) {
  const response: any = await axios.post('/v2/materials/range/customers-tradersites', items);
  if (response.status === 201) return true;
  return false;
}

export async function getRangeCustomerTradersitesWithConditions({
  page = 1,
  itemsPerPage = 0,
  query = '',
  sortColumn,
  sortAscending,
  isActive,
  rangeId = null,
  customerId = null,
}: TableFilterData & { rangeId?: string | null; customerId?: string | null }): Promise<{
  data: RangeCustomerTradersite[];
  count: number;
}> {
  const skip = (page - 1) * itemsPerPage;

  let url = `/v2/materials/range/customers-tradersites/search?take=${itemsPerPage}&skip=${skip}`;
  if (rangeId != null) url += `&rangeId=${rangeId}`;
  if (customerId != null) url += `&customerId=${customerId}`;

  const response = await axios.post(url, {
    searchText: query,
    sortColumn,
    sortAscending,
    isActive,
    rangeId,
    customerId,
  });

  const valid = validate(getRangeCustomerTradersitesSchema, response.data.data);
  return { data: valid, count: response.data.count };
}

export async function checkAndCreateRangeCustomerTradersite(
  customerId: string,
  traderSiteId: string
): Promise<boolean> {
  const result = await getRangeCustomerTradersitesWithConditions({
    page: 1,
    itemsPerPage: 1,
    customerId,
  });

  const exists = result.data.some(
    (item) => item.customerId === customerId && item.traderSiteId === traderSiteId
  );

  if (!exists) {
    const newRecord: RangeCustomerTradersite = {
      id: uuidv4(),
      customerId,
      traderSiteId,
      hasDynamicRange: true,
      isActive: true,
      isFromManager: true,
      recordMode: RecordMode.Added,
    };

    await addEditRangeCustomerTradersites([newRecord]);
    return true;
  }

  return false;
}
